import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSaved, deleteSaved } from "@/lib/storage";
import { Bookmark, Trash2, ChevronDown, ChevronUp, FileDown, FileText, X, ShieldCheck, Table } from "lucide-react";
import { toast } from "sonner";
import { exportCalculationToPDF, exportDailySheetToPDF } from "@/lib/pdfExport";
import { getDisplayUnit, fromImperialValue, convertLabeledMap } from "@/lib/metricUnits";
import { useEntitlement } from "@/lib/entitlement";
import { isProActive, FeatureLockedError } from "@/lib/featureAccess";
import UpgradeButton from "@/components/UpgradeButton";

function _toastUpgrade(navigate, message) {
  toast.error(message || "This action requires RigSentinel Pro.", {
    duration: 5000,
    action: { label: "Upgrade", onClick: () => navigate("/pricing") },
  });
}

const ALL = "__all__";
const RANGE_MS = {
  today: 24 * 3600 * 1000,
  "7d": 7 * 24 * 3600 * 1000,
  "30d": 30 * 24 * 3600 * 1000,
};
const RANGE_LABEL = { all: "All time", today: "Last 24 hours", "7d": "Last 7 days", "30d": "Last 30 days" };
// Worksheet calc IDs whose results carry a DRAFT compliance warning
const WORKSHEET_IDS = new Set(["maasp_worksheet", "pump_pressure_schedule", "well_kill_worksheet", "shut_in_sheet"]);
const isWorksheet = (it) => !!it.worksheetWarning || WORKSHEET_IDS.has(it.calcId);

export default function Saved() {
  const navigate = useNavigate();
  const ent = useEntitlement();
  const isPro = isProActive(ent);
  const [items, setItems] = useState(getSaved());
  const [openId, setOpenId] = useState(null);
  const [wellFilter, setWellFilter] = useState(ALL);
  const [rigFilter, setRigFilter] = useState(ALL);
  const [range, setRange] = useState("all"); // all | today | 7d | 30d

  const wells = useMemo(() => {
    const s = new Set();
    items.forEach((x) => { if (x.wellName) s.add(x.wellName); });
    return Array.from(s).sort();
  }, [items]);

  const rigs = useMemo(() => {
    const s = new Set();
    items.forEach((x) => { if (x.rigName) s.add(x.rigName); });
    return Array.from(s).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const now = Date.now();
    return items.filter((x) => {
      if (wellFilter !== ALL && x.wellName !== wellFilter) return false;
      if (rigFilter !== ALL && x.rigName !== rigFilter) return false;
      if (range !== "all") {
        const ageMs = now - new Date(x.createdAt).getTime();
        if (ageMs > RANGE_MS[range]) return false;
      }
      return true;
    });
  }, [items, wellFilter, rigFilter, range]);

  const resetFilters = () => { setWellFilter(ALL); setRigFilter(ALL); setRange("all"); };

  const handleDelete = (id) => {
    try {
      deleteSaved(id);
      setItems(getSaved());
      toast.success("Deleted");
    } catch (e) {
      if (e instanceof FeatureLockedError) { _toastUpgrade(navigate, e.message); return; }
      throw e;
    }
  };

  const handleDailySheet = () => {
    if (filtered.length === 0) { toast.error("No calculations match the current filter"); return; }
    try {
      const fileLabel = [
        wellFilter !== ALL ? wellFilter : null,
        rigFilter !== ALL ? rigFilter : null,
        range !== "all" ? range : null,
      ].filter(Boolean).join("-") || "daily-sheet";
      exportDailySheetToPDF(filtered, {
        wellName: wellFilter === ALL ? null : wellFilter,
        rigName: rigFilter === ALL ? null : rigFilter,
        dateRangeLabel: RANGE_LABEL[range],
        fileLabel,
      });
      toast.success(`Daily Sheet PDF · ${filtered.length} calculation${filtered.length === 1 ? "" : "s"}`);
    } catch (e) {
      if (e instanceof FeatureLockedError) { _toastUpgrade(navigate, e.message); return; }
      toast.error(e.message || "PDF export failed");
    }
  };

  const worksheetItems = useMemo(() => filtered.filter(isWorksheet), [filtered]);

  const handleWorksheetPack = () => {
    if (worksheetItems.length === 0) { toast.error("No worksheet items in the current filter"); return; }
    try {
      const fileLabel = "worksheet-pack-" + ([
        wellFilter !== ALL ? wellFilter : null,
        rigFilter !== ALL ? rigFilter : null,
      ].filter(Boolean).join("-") || "all");
      exportDailySheetToPDF(worksheetItems, {
        wellName: wellFilter === ALL ? null : wellFilter,
        rigName: rigFilter === ALL ? null : rigFilter,
        dateRangeLabel: `${RANGE_LABEL[range]} · Worksheet Pack (compliance binder)`,
        fileLabel,
      });
      toast.success(`Worksheet Pack · ${worksheetItems.length} sheet${worksheetItems.length === 1 ? "" : "s"}`);
    } catch (e) {
      if (e instanceof FeatureLockedError) { _toastUpgrade(navigate, e.message); return; }
      toast.error(e.message || "PDF export failed");
    }
  };

  const handleCSV = () => {
    if (filtered.length === 0) { toast.error("No calculations match the current filter"); return; }
    const csvEscape = (s) => {
      const v = String(s ?? "");
      return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    };
    const header = ["Timestamp", "Calculator", "Category", "Stack Mode", "Well", "Rig", "Result", "Unit", "Formula", "Note", "System"];
    const rows = filtered.map((it) => {
      const sys = it.system || "imperial";
      // Prefer stored displayResult/displayUnit; else convert from imperial canonical.
      const dispResult = it.displayResult !== undefined && it.displayResult !== null
        ? it.displayResult
        : fromImperialValue(it.result, it.unit, sys);
      const dispUnit = it.displayUnit || getDisplayUnit(it.unit, sys);
      return [
        new Date(it.createdAt).toISOString(),
        it.calcName, it.category || "", it.stackMode || "", it.wellName || "", it.rigName || "",
        dispResult, dispUnit, it.formula, it.note || "", sys,
      ].map(csvEscape).join(",");
    });
    const blob = new Blob([header.join(",") + "\n" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rigsentinel-saved-${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`CSV exported · ${filtered.length} row${filtered.length === 1 ? "" : "s"}`);
  };

  const filtersActive = wellFilter !== ALL || rigFilter !== ALL || range !== "all";

  return (
    <div className="flex flex-col gap-5" data-testid="saved-page">
      <section>
        <div className="font-mono-rs text-[10px] tracking-[0.22em] uppercase text-[var(--rs-amber)]">Saved Sheets</div>
        <h2 className="font-heading font-black text-3xl tracking-tight mt-1">Calculation History</h2>
        <p className="text-sm text-[var(--rs-text-2)] mt-1.5">Stored locally on this device.</p>
      </section>

      {!isPro && (
        <section
          className="card-rs p-3 flex items-center justify-between gap-3"
          data-testid="saved-preview-banner"
          style={{ borderColor: "rgba(255,179,0,0.45)" }}
        >
          <div className="text-[12px] text-[var(--rs-text-2)]">
            <span className="label-up text-[10px] text-[var(--rs-amber)]">Preview · read-only</span>
            <div className="mt-0.5">Saving new calculations and editing saved sheets is available with RigSentinel Pro.</div>
          </div>
          <UpgradeButton testid="saved-preview-upgrade" size="sm" />
        </section>
      )}

      {items.length > 0 && (
        <>
          {/* Daily Sheet generator */}
          <section className="card-rs p-4" data-testid="daily-sheet-card">
            <div className="flex items-baseline justify-between mb-3">
              <div className="label-up text-[var(--rs-amber)]">Daily Operations Sheet</div>
              {filtersActive && (
                <button
                  data-testid="reset-filters"
                  onClick={resetFilters}
                  className="text-[10px] text-[var(--rs-text-3)] inline-flex items-center gap-1 hover:text-[var(--rs-amber)]"
                >
                  <X className="w-3 h-3" /> Reset filters
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] label-up text-[var(--rs-text-3)] mb-1">Well</label>
                <select
                  data-testid="filter-well"
                  className="input-rs h-10"
                  value={wellFilter}
                  onChange={(e) => setWellFilter(e.target.value)}
                >
                  <option value={ALL}>All wells</option>
                  {wells.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] label-up text-[var(--rs-text-3)] mb-1">Rig</label>
                <select
                  data-testid="filter-rig"
                  className="input-rs h-10"
                  value={rigFilter}
                  onChange={(e) => setRigFilter(e.target.value)}
                >
                  <option value={ALL}>All rigs</option>
                  {rigs.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            <div className="mt-3">
              <label className="block text-[10px] label-up text-[var(--rs-text-3)] mb-1.5">Date Range</label>
              <div className="flex gap-2 overflow-x-auto hide-sb -mx-1 px-1">
                {["all", "today", "7d", "30d"].map((r) => (
                  <button
                    key={r}
                    data-testid={`range-${r}`}
                    onClick={() => setRange(r)}
                    data-active={range === r}
                    className="unit-pill whitespace-nowrap"
                  >
                    {{ all: "All time", today: "24h", "7d": "7 days", "30d": "30 days" }[r]}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between mt-4 pt-3 border-t border-[var(--rs-border)]">
              <div className="text-xs">
                <span data-testid="filtered-count" className="font-mono-rs text-lg font-bold text-[var(--rs-text)]">{filtered.length}</span>
                <span className="text-[var(--rs-text-3)] ml-1.5">of {items.length} included</span>
              </div>
              <button
                data-testid="daily-sheet-btn"
                onClick={handleDailySheet}
                disabled={filtered.length === 0}
                className={`h-11 px-4 rounded-md label-up text-xs inline-flex items-center gap-2 ${
                  filtered.length === 0 ? "btn-ghost opacity-50 cursor-not-allowed" : "btn-amber"
                }`}
              >
                <FileText className="w-4 h-4" /> Generate Daily Sheet
              </button>
            </div>

            {/* Worksheet Pack preset — filters only DRAFT-warning items for compliance binders */}
            <div className="mt-3 pt-3 border-t border-[var(--rs-border)] flex gap-2 flex-wrap" data-testid="extra-exports">
              <button
                data-testid="csv-export-btn"
                onClick={handleCSV}
                disabled={filtered.length === 0}
                className={`h-10 px-3 rounded-md label-up text-xs inline-flex items-center gap-2 shrink-0 ${
                  filtered.length === 0 ? "btn-ghost opacity-50 cursor-not-allowed" : "btn-ghost"
                }`}
              >
                <Table className="w-4 h-4" /> CSV
              </button>
              {wellFilter !== ALL && (
                <button
                  data-testid="open-well-dashboard"
                  onClick={() => navigate(`/well/${encodeURIComponent(wellFilter)}`)}
                  className="h-10 px-3 rounded-md label-up text-xs inline-flex items-center gap-2 shrink-0 btn-ghost"
                  style={{ borderColor: "var(--rs-amber)", color: "var(--rs-amber)" }}
                >
                  <ShieldCheck className="w-4 h-4" /> Open {wellFilter} Dashboard
                </button>
              )}
            </div>

            <div className="mt-3 pt-3 border-t border-[var(--rs-border)]" data-testid="worksheet-pack-row">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-3.5 h-3.5 text-[var(--rs-amber)] shrink-0" />
                    <span className="label-up text-[10px] text-[var(--rs-amber)]">Worksheet Pack</span>
                  </div>
                  <div className="text-[11px] text-[var(--rs-text-3)] mt-1 leading-relaxed">
                    Bundle only DRAFT worksheets (MAASP, Pump Schedule, Well Kill, Shut-In) for a compliance binder.
                    <span className="block mt-0.5 italic">Older saved entries are auto-detected by calculator type.</span>
                  </div>
                  <div className="text-[11px] text-[var(--rs-text-2)] mt-1">
                    <span data-testid="worksheet-count" className="font-mono-rs text-[var(--rs-text)] font-bold">{worksheetItems.length}</span>
                    <span className="text-[var(--rs-text-3)] ml-1">worksheet{worksheetItems.length === 1 ? "" : "s"} in current filter</span>
                  </div>
                </div>
                <button
                  data-testid="worksheet-pack-btn"
                  onClick={handleWorksheetPack}
                  disabled={worksheetItems.length === 0}
                  className={`h-10 px-3 rounded-md label-up text-xs inline-flex items-center gap-2 shrink-0 ${
                    worksheetItems.length === 0 ? "btn-ghost opacity-50 cursor-not-allowed" : "btn-ghost border-[var(--rs-amber)]"
                  }`}
                  style={worksheetItems.length > 0 ? { borderColor: "var(--rs-amber)", color: "var(--rs-amber)" } : {}}
                >
                  <FileText className="w-4 h-4" /> Pack PDF
                </button>
              </div>
            </div>
          </section>
        </>
      )}

      {items.length === 0 ? (
        <div className="card-rs p-6 text-center" data-testid="saved-empty">
          <Bookmark className="w-7 h-7 mx-auto text-[var(--rs-text-3)] mb-2" />
          <div className="text-sm text-[var(--rs-text-2)]">No saved calculations yet.</div>
          <div className="text-xs text-[var(--rs-text-3)] mt-1">Run a calculator and tap Save.</div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {filtered.length === 0 ? (
            <div className="card-rs p-6 text-center" data-testid="filtered-empty">
              <div className="text-sm text-[var(--rs-text-2)]">No calculations match the current filters.</div>
            </div>
          ) : (
            filtered.map((it) => {
              const expanded = openId === it.id;
              const sys = it.system || "imperial";
              const dispResult = it.displayResult !== undefined && it.displayResult !== null
                ? it.displayResult
                : fromImperialValue(it.result, it.unit, sys);
              const dispUnit = it.displayUnit || getDisplayUnit(it.unit, sys);
              const dispInputs = convertLabeledMap(it.inputs, sys);
              return (
                <div key={it.id} className="card-rs p-3" data-testid={`saved-${it.id}`}>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setOpenId(expanded ? null : it.id)}
                      className="flex-1 min-w-0 flex items-center justify-between text-left"
                    >
                      <div className="min-w-0 pr-3">
                        <div className="font-semibold text-sm truncate">{it.calcName}</div>
                        <div className="text-[11px] text-[var(--rs-text-3)] mt-0.5">
                          {new Date(it.createdAt).toLocaleString()}
                          {it.wellName ? ` · ${it.wellName}` : ""}
                          {it.rigName ? ` · ${it.rigName}` : ""}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="font-mono-rs text-sm text-[var(--rs-amber)]">
                          {String(it.result)} <span className="text-[var(--rs-text-3)]">{it.unit}</span>
                        </span>
                        {expanded ? <ChevronUp className="w-4 h-4 text-[var(--rs-text-3)]" /> : <ChevronDown className="w-4 h-4 text-[var(--rs-text-3)]" />}
                      </div>
                    </button>
                    <button
                      data-testid={`pdf-${it.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        try { exportCalculationToPDF(it); toast.success("PDF downloaded"); }
                        catch (err) {
                          if (err instanceof FeatureLockedError) { _toastUpgrade(navigate, err.message); return; }
                          toast.error("PDF export failed");
                        }
                      }}
                      className="btn-ghost h-9 w-9 rounded-md inline-flex items-center justify-center shrink-0"
                      aria-label="Download PDF"
                    >
                      <FileDown className="w-4 h-4" />
                    </button>
                  </div>

                  {expanded && (
                    <div className="mt-3 border-t border-[var(--rs-border)] pt-3 flex flex-col gap-2">
                      <Row label="Formula"><span className="font-mono-rs text-xs">{it.formula}</span></Row>
                      <Row label="Inputs">
                        <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                          {Object.entries(dispInputs).map(([k, v]) => (
                            <div key={k} className="flex justify-between text-xs">
                              <span className="text-[var(--rs-text-3)]">{k}</span>
                              <span className="font-mono-rs">{String(v)}</span>
                            </div>
                          ))}
                        </div>
                      </Row>
                      <Row label="Steps">
                        <ol className="font-mono-rs text-xs text-[var(--rs-text-2)] space-y-0.5">
                          {it.steps.map((s, i) => (
                            <li key={i}><span className="text-[var(--rs-amber)] mr-1.5">{String(i + 1).padStart(2, "0")}</span>{s}</li>
                          ))}
                        </ol>
                      </Row>
                      {it.note && <Row label="Note"><span className="text-xs">{it.note}</span></Row>}
                      <div className="flex justify-end gap-2 pt-1">
                        <button
                          data-testid={`del-${it.id}`}
                          onClick={() => handleDelete(it.id)}
                          className="btn-ghost h-9 px-3 rounded-md inline-flex items-center gap-2 text-xs label-up"
                          style={{ borderColor: "#C62828", color: "#FCA5A5" }}
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <div className="label-up text-[var(--rs-text-3)] text-[10px] mb-1">{label}</div>
      {children}
    </div>
  );
}
